import { useState } from 'react';
import { X, Plus, Trash2, Save } from 'lucide-react';
import { ArticleOutline } from '../../lib/content-generation/types';

interface OutlineEditorProps {
  outline: ArticleOutline;
  onApprove: (editedOutline: ArticleOutline) => void;
  onCancel: () => void;
}

export function OutlineEditor({ outline, onApprove, onCancel }: OutlineEditorProps) {
  const [editedOutline, setEditedOutline] = useState<ArticleOutline>({
    ...JSON.parse(JSON.stringify(outline)),
    // Встановлюємо значення за замовчуванням для toggles якщо їх немає
    includeIntroduction: outline.includeIntroduction !== undefined ? outline.includeIntroduction : true,
    includeConclusion: outline.includeConclusion !== undefined ? outline.includeConclusion : true,
    includeCta: outline.includeCta !== undefined ? outline.includeCta : true,
    includeFaq: outline.includeFaq !== undefined ? outline.includeFaq : true,
  });

  const handleH1Change = (value: string) => {
    setEditedOutline(prev => ({ ...prev, h1: value }));
  };

  const handleMetaDescriptionChange = (value: string) => {
    setEditedOutline(prev => ({ ...prev, metaDescription: value }));
  };

  const handleIntroH3Change = (index: number, value: string) => {
    setEditedOutline(prev => ({
      ...prev,
      introduction: {
        ...prev.introduction,
        h3: prev.introduction.h3.map((h3, i) => i === index ? value : h3)
      }
    }));
  };

  const handleSectionH2Change = (sectionIndex: number, value: string) => {
    setEditedOutline(prev => ({
      ...prev,
      sections: prev.sections.map((section, i) => 
        i === sectionIndex ? { ...section, h2: value } : section
      )
    }));
  };

  const handleSectionH3Change = (sectionIndex: number, h3Index: number, value: string) => {
    setEditedOutline(prev => ({
      ...prev,
      sections: prev.sections.map((section, i) => 
        i === sectionIndex 
          ? { ...section, h3: section.h3.map((h3, j) => j === h3Index ? value : h3) }
          : section
      )
    }));
  };

  const addSectionH3 = (sectionIndex: number) => {
    setEditedOutline(prev => ({
      ...prev,
      sections: prev.sections.map((section, i) => 
        i === sectionIndex 
          ? { ...section, h3: [...section.h3, 'New H3'] }
          : section
      )
    }));
  };

  const removeSectionH3 = (sectionIndex: number, h3Index: number) => {
    setEditedOutline(prev => ({
      ...prev,
      sections: prev.sections.map((section, i) => 
        i === sectionIndex 
          ? { ...section, h3: section.h3.filter((_, j) => j !== h3Index) }
          : section
      )
    }));
  };

  const addSection = () => {
    setEditedOutline(prev => ({
      ...prev,
      sections: [
        ...prev.sections,
        {
          h2: 'New Section',
          h3: ['Subsection 1'],
          targetKeywords: {
            primary: outline.sections[0]?.targetKeywords.primary || '',
            secondary: []
          },
          estimatedWordCount: 600
        }
      ]
    }));
  };

  const removeSection = (sectionIndex: number) => {
    setEditedOutline(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== sectionIndex)
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Edit Article Structure</h2>
            <p className="text-sm text-gray-500 mt-1">Modify headings before generating content</p>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* H1 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              H1 Title
            </label>
            <input
              type="text"
              value={editedOutline.h1}
              onChange={(e) => handleH1Change(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Meta Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Meta Description
            </label>
            <textarea
              value={editedOutline.metaDescription}
              onChange={(e) => handleMetaDescriptionChange(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Toggles для керування секціями */}
          <div className="border border-gray-200 rounded-lg p-4 bg-blue-50">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Article Structure Options</h3>
            <div className="space-y-3">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editedOutline.includeIntroduction}
                  onChange={(e) => setEditedOutline(prev => ({ ...prev, includeIntroduction: e.target.checked }))}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700">Include Introduction (no H2)</span>
              </label>
              
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editedOutline.includeConclusion}
                  onChange={(e) => setEditedOutline(prev => ({ ...prev, includeConclusion: e.target.checked }))}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700">Include Conclusion</span>
              </label>
              
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editedOutline.includeCta}
                  onChange={(e) => setEditedOutline(prev => ({ ...prev, includeCta: e.target.checked }))}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700">Include CTA (Call to Action)</span>
              </label>
              
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editedOutline.includeFaq}
                  onChange={(e) => setEditedOutline(prev => ({ ...prev, includeFaq: e.target.checked }))}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700">Include FAQ</span>
              </label>
            </div>
          </div>

          {/* Introduction - without H3 subsections */}
          {editedOutline.introduction && editedOutline.includeIntroduction && (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h3 className="text-lg font-medium text-gray-900">Introduction (no H2)</h3>
              <p className="text-sm text-gray-500 mt-1">Introduction section will be generated automatically</p>
            </div>
          )}

          {/* Main Sections */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Main Sections</h3>
              <button
                onClick={addSection}
                className="inline-flex items-center px-3 py-1 text-sm font-medium text-indigo-600 hover:text-indigo-800"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Section
              </button>
            </div>

            {editedOutline.sections.map((section, sectionIndex) => (
              <div key={sectionIndex} className="border border-gray-200 rounded-lg p-4 bg-white">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Section {sectionIndex + 1} - H2
                    </label>
                    <input
                      type="text"
                      value={section.h2}
                      onChange={(e) => handleSectionH2Change(sectionIndex, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <button
                    onClick={() => removeSection(sectionIndex)}
                    className="ml-2 mt-7 text-red-600 hover:text-red-800"
                    title="Remove section"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                {/* H3s */}
                <div className="space-y-2 mt-3">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700">H3 Subsections</label>
                    <button
                      onClick={() => addSectionH3(sectionIndex)}
                      className="text-xs text-indigo-600 hover:text-indigo-800"
                    >
                      <Plus className="h-3 w-3 inline mr-1" />
                      Add H3
                    </button>
                  </div>
                  {section.h3.map((h3, h3Index) => (
                    <div key={h3Index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={h3}
                        onChange={(e) => handleSectionH3Change(sectionIndex, h3Index, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                        placeholder={`H3 #${h3Index + 1}`}
                      />
                      <button
                        onClick={() => removeSectionH3(sectionIndex, h3Index)}
                        className="text-red-600 hover:text-red-800"
                        title="Remove H3"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Conclusion */}
          {editedOutline.conclusion && editedOutline.includeConclusion && (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h3 className="text-lg font-medium text-gray-900">Conclusion</h3>
              <p className="text-sm text-gray-500 mt-1">Conclusion section will be generated automatically</p>
            </div>
          )}

          {/* CTA */}
          {editedOutline.cta && editedOutline.includeCta && (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h3 className="text-lg font-medium text-gray-900">Call to Action</h3>
              <p className="text-sm text-gray-500 mt-1">CTA section will be generated automatically</p>
            </div>
          )}

          {/* FAQ */}
          {editedOutline.includeFaq && (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h3 className="text-lg font-medium text-gray-900">FAQ (H2 Section)</h3>
              <p className="text-sm text-gray-500 mt-1">FAQ section with 5-7 questions will be generated automatically</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancel
          </button>
          <button
            onClick={() => onApprove(editedOutline)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Save className="h-4 w-4 mr-2" />
            Approve & Continue
          </button>
        </div>
      </div>
    </div>
  );
}
